import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useUser } from "contexts/UserContext";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
import queryBlockedIntervals from "../../../../../../services/scheduler/queryBlockedIntervals";
import { useCalendarTime } from "./CalendarTimeContext";
import isGCalEvent from "utils/isGCalEvent";

type Context = {
  gcalEvents: BlockedInterval[];
  blockedIntervals: BlockedInterval[];
  forceReload: () => void;
  showGCal: boolean | undefined;
  setShowGCal: Dispatch<SetStateAction<boolean | undefined>>;
  showBlockedIntervals: boolean;
  setShowBlockedIntervals: Dispatch<SetStateAction<boolean>>;
  setVisibleCalendarIds: Dispatch<SetStateAction<string[]>>;
};

const BlockedIntervalsContext = createContext<Context>({
  gcalEvents: [],
  blockedIntervals: [],
  forceReload: () => null,
  showGCal: false,
  setShowGCal: () => null,
  showBlockedIntervals: false,
  setShowBlockedIntervals: () => null,
  setVisibleCalendarIds: () => null,
});

type Props = { children: ReactNode };

export const BlockedIntervalsContextProvider = ({ children }: Props) => {
  const { user } = useUser();
  const { practiceOwnerOrchidUserId } = useTeamContext();
  const { timeRange } = useCalendarTime();
  const [blockedIntervals, setBlockedIntervals] = useState<BlockedInterval[]>(
    [],
  );

  // Google Calendar events within display time range
  const [gcalEvents, setGCalEvents] = useState<BlockedInterval[]>([]);

  const [reload, setReload] = useState(0);
  const forceReload = useCallback(() => setReload((s) => s + 1), []);

  const [visibleCalendarIds, setVisibleCalendarIds] = useState<string[]>([]);

  // get blocked intervals on init
  useEffect(() => {
    if (!user || !timeRange || timeRange.length < 2) {
      return;
    }
    queryBlockedIntervals(timeRange, practiceOwnerOrchidUserId)
      .then((res) => {
        const nextGCalEvents: typeof gcalEvents = [];

        for (let i = 0; i < res.length; i++) {
          if (isGCalEvent(res[i])) {
            const arr = res.splice(i, 1);
            nextGCalEvents.push(...arr);
            i--;
          }
        }

        setGCalEvents(
          nextGCalEvents.filter(
            (event) =>
              event.calendar_id &&
              visibleCalendarIds.includes(event.calendar_id),
          ),
        );
        setBlockedIntervals(res);
      })
      .catch(console.error);
  }, [user, timeRange, reload, visibleCalendarIds, practiceOwnerOrchidUserId]);

  const [showGCal, setShowGCal] = useState<boolean | undefined>(true);
  const [showBlockedIntervals, setShowBlockedIntervals] = useState(true);

  // useEffect(() => {
  //   setShowGCal(!!user?.has_calendar_google_connected);
  // }, [user?.has_calendar_google_connected]);

  return (
    <BlockedIntervalsContext.Provider
      value={{
        gcalEvents: showGCal ? gcalEvents : [],
        showGCal,
        setShowGCal,
        blockedIntervals: showBlockedIntervals ? blockedIntervals : [],
        showBlockedIntervals,
        setShowBlockedIntervals,
        forceReload,
        setVisibleCalendarIds,
      }}
    >
      {children}
    </BlockedIntervalsContext.Provider>
  );
};

export const useBlockedIntervals = () => useContext(BlockedIntervalsContext);
